import { useTranslation } from "react-i18next";
import { cx } from "@/lib/style/cva.config";

/**
 * De vorm van wat er aankomt, terwijl het onderweg is.
 *
 * De lijstschermen lieten tijdens het laden niets zien en klapten daarna in één keer vol. Op de
 * wifi van een evenement is dat een leeg scherm van een paar tellen -- en een leeg scherm leest als
 * "er is niets voor mij" of als "de app doet het niet", allebei net op het moment dat iemand wil
 * weten hoe laat hij moet beginnen.
 *
 * Een skelet zegt iets anders: er komt een lijst, hij ziet er zo uit, hij is er zo. Het houdt ook
 * de hoogte vast, zodat het scherm bij aankomst niet meer opspringt.
 *
 * Bewust een suggestie van de vorm en geen exacte kopie van elke kaart: een skelet dat de kaart tot
 * op de pixel nabootst, moet mee veranderen met elke kaart, en dan klopt hij binnen een maand niet
 * meer. Twee regels en een blok zijn genoeg om "lijst" te betekenen.
 */
export function Skeleton(props: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cx("block animate-pulse rounded-md bg-surface-secondary", props.className)}
    />
  );
}

/**
 * Een paar kaarten die er nog niet zijn.
 *
 * `role="status"` en niet alleen de blokken hierboven: een schermlezer heeft niets aan grijze
 * vlakken. Die hoort "laden" en verder stilte, en krijgt de lijst zodra hij er is.
 */
export function ListSkeleton(props: { count?: number; className?: string }) {
  const { t } = useTranslation();
  const count = props.count ?? 3;

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t("common:loading")}
      className={cx("flex flex-col gap-4", props.className)}
    >
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="flex flex-col gap-3 rounded-xl border border-bordercolor-primary p-4"
        >
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-3 w-4/5" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}
